// =====================================================================
// Client-Side App-Registry
// =====================================================================
// Single Source of Truth fuer Rail-Items, Routen und Iframe-Embedding.
// Die `appKey`-Werte muessen mit `SATELLITE_APPS[].appClaim` in
// `server/index.ts` und mit `public.apps.key` uebereinstimmen.
//
// `route` ist der Hash-Router-Pfad (z.B. `/notes`).
// `embedSrc` ist die Iframe-URL bei Erstaufruf (in der Regel der
// SSO-Launch unter `/launch/<basePath>`, fuer statische Hub-Seiten der
// direkte Pfad).
// `appKey` ist null fuer Hub-eigene Views (Launcher) bzw. fuer Apps die
// kein RBAC-Gate haben — sonst der Key gegen `loadUserApps()`.
// `iconSrc` zeigt auf das gerenderte App-Icon (SVG) in client/public/icons/
// und wird in der Rail als <img> 40×40 dargestellt.
// =====================================================================

import { INSTANCE } from "./instance";
import { orderBySatellites } from "../../../shared/instance";
import { getAppLabel, isAppEnabledForInstance } from "../../../shared/app-catalog";

export type ShellApp = {
  id: string;            // interner Shell-App-Key (z.B. "notes", "hub")
  title: string;
  iconSrc: string;       // Pfad zum SVG-App-Icon (Rail/Tile)
  route: string;         // Hash-Router-Pfad
  appKey: string | null; // public.apps.key fuer RBAC; null = keine Pruefung
  kind: "hub" | "satellite" | "static" | "external";
  showInRail?: boolean;  // false = routebar, aber nicht in der linken App-Rail
  // Iframe-Defaults — nur fuer satellite/static relevant.
  defaultEmbedSrc?: string; // initial geladene URL im Iframe
  basePath?: string;     // Public-Pfad-Prefix; gespeicherter lastView muss damit beginnen
  externalUrl?: string;  // kind === "external": Ziel-URL
};

const ALL_SHELL_APPS: ShellApp[] = [
  {
    id: "hub",
    title: "Hub · Launcher",
    iconSrc: "/icons/clean-glass/v1/hub/icon.svg",
    route: "/hub",
    appKey: null,
    kind: "hub",
  },
  {
    id: "notes",
    title: getAppLabel("notes"),
    iconSrc: "/icons/clean-glass/v1/nexus/icon.svg",
    route: "/nexus",
    appKey: "notes",
    kind: "satellite",
    defaultEmbedSrc: "/launch/nexus",
    basePath: "/nexus",
  },
  {
    id: "liquid",
    title: getAppLabel("liquid"),
    iconSrc: "/icons/clean-glass/v1/liquid/icon.svg",
    route: "/liquid",
    appKey: "liquid",
    kind: "satellite",
    defaultEmbedSrc: "/launch/liquid",
    basePath: "/liquid",
  },
  {
    id: "money",
    title: getAppLabel("money"),
    iconSrc: "/icons/clean-glass/v1/money/icon.svg",
    route: "/money",
    appKey: "money",
    kind: "satellite",
    defaultEmbedSrc: "/launch/money",
    basePath: "/money",
  },
  {
    id: "coorconn-liquid",
    title: getAppLabel("coorconn-liquid"),
    iconSrc: "/icons/clean-glass/v1/coorconn-liquid/icon.svg",
    route: "/coorconn-liquid",
    appKey: "coorconn-liquid",
    kind: "satellite",
    defaultEmbedSrc: "/launch/coorconn-liquid",
    basePath: "/coorconn-liquid",
  },
  {
    id: "briefing",
    title: getAppLabel("briefing"),
    iconSrc: "/icons/clean-glass/v1/briefing/icon.svg",
    route: "/briefing",
    appKey: "briefing",
    kind: "satellite",
    defaultEmbedSrc: "/launch/briefing",
    basePath: "/briefing",
  },
  {
    id: "life",
    title: getAppLabel("life"),
    iconSrc: "/icons/clean-glass/v1/life/icon.svg",
    route: "/life",
    appKey: "life",
    kind: "satellite",
    defaultEmbedSrc: "/launch/life",
    basePath: "/life",
  },
  {
    id: "scan",
    title: getAppLabel("scan"),
    iconSrc: "/icons/clean-glass/v1/scan/icon.svg",
    route: "/scan",
    appKey: "scan",
    kind: "satellite",
    defaultEmbedSrc: "/launch/scan",
    basePath: "/scan",
  },
  {
    id: "fair",
    title: getAppLabel("fair-kapa"),
    iconSrc: "/icons/clean-glass/v1/fair-kapa/icon.svg",
    route: "/fair",
    appKey: "fair-kapa",
    kind: "satellite",
    defaultEmbedSrc: "/launch/fair-kapa",
    basePath: "/fair-kapa",
  },
  {
    id: "vertrag",
    title: getAppLabel("vertrag"),
    iconSrc: "/icons/clean-glass/v1/vertrag/icon.svg",
    route: "/vertrag",
    appKey: "vertrag",
    kind: "satellite",
    defaultEmbedSrc: "/launch/vertrag",
    basePath: "/vertrag",
  },

  {
    id: "back-office",
    title: getAppLabel("back-office"),
    iconSrc: "/icons/clean-glass/v1/backoffice/icon.svg",
    route: "/backoffice",
    appKey: "back-office",
    kind: "satellite",
    defaultEmbedSrc: "/launch/backoffice",
    basePath: "/backoffice",
  },
  {
    id: "story",
    title: getAppLabel("story"),
    iconSrc: "/icons/clean-glass/v1/story/icon.svg",
    route: "/story",
    appKey: "story",
    kind: "satellite",
    defaultEmbedSrc: "/launch/story",
    basePath: "/story",
  },
  {
    id: "regelwerke",
    title: getAppLabel("regelwerke"),
    iconSrc: "/icons/clean-glass/v1/regelwerke/icon.svg",
    route: "/regelwerke",
    appKey: "regelwerke",
    kind: "satellite",
    defaultEmbedSrc: "/launch/regelwerke",
    basePath: "/regelwerke",
  },
  {
    id: "time",
    title: getAppLabel("time"),
    iconSrc: "/icons/clean-glass/v1/time/icon.svg",
    route: "/time",
    appKey: "time",
    kind: "satellite",
    defaultEmbedSrc: "/launch/time",
    basePath: "/time",
  },
];

// SATELLITES (Instanzkonfiguration): Auswahl und Reihenfolge der Satelliten
// in Rail, Home Kreis und Routen. Ohne Variable alle in obiger Reihenfolge.
export const SHELL_APPS: ShellApp[] = orderBySatellites(
  ALL_SHELL_APPS.filter((app) => app.kind !== "satellite" || (app.appKey !== null && isAppEnabledForInstance(app.appKey, INSTANCE))),
  INSTANCE.satellites,
  // App-ID `fair` ist eine reine UI-/Routing-ID; Instanzfilter nutzen den
  // stabilen DB-/SSO-Claim `fair-kapa`.
  (app) => app.appKey ?? app.id,
  (app) => app.kind === "satellite",
);

export function findShellApp(id: string): ShellApp | undefined {
  return SHELL_APPS.find((a) => a.id === id);
}

export function findShellAppByRoute(route: string): ShellApp | undefined {
  return SHELL_APPS.find(
    (a) => a.route === route || route.startsWith(a.route + "/"),
  );
}

export function getDefaultLandingApp(
  allowed: ReadonlySet<string> | null | undefined,
): ShellApp | undefined {
  return SHELL_APPS.find((app) => {
    if (app.id === "hub" || app.showInRail === false) return false;
    if (app.appKey == null) return true;
    if (allowed == null) return false;
    return allowed.has(app.appKey);
  });
}

export function getDefaultLandingRoute(
  _allowed: ReadonlySet<string> | null | undefined,
): string {
  // Der Hub ist die zentrale Arbeitsoberflaeche: Unabhaengig von Rolle oder
  // App-Freigaben beginnt jede regulaere Sitzung auf der Launcher-Home-Seite.
  // Sonderfaelle wie Passwortwechsel werden vor dieser Funktion behandelt.
  return "/home";
}

// Localstorage-Helper fuer Last-View pro App
const LAST_VIEW_PREFIX = "lastView:";

export function loadLastView(appId: string): string | null {
  try {
    return window.localStorage.getItem(LAST_VIEW_PREFIX + appId);
  } catch {
    return null;
  }
}

export function saveLastView(appId: string, route: string): void {
  try {
    window.localStorage.setItem(LAST_VIEW_PREFIX + appId, route);
  } catch {
    /* ignore quota / privacy errors */
  }
}
